/**
 * OneTimeActivityGrid — replaces the standard Subtask grid for "GST One-Time
 * Activities" board subtasks. Each row is a discrete instance (not a client),
 * so the same client may appear multiple times.
 *
 * Columns are kind-aware: e.g. Notice shows notice_no + due_date prominently;
 * Registration shows GSTIN + category + registration_date.
 *
 * Status changes that trigger auto-actions (e.g. Registration approval issuing
 * a GSTIN) are persisted via PUT /instances/{id} which validates required
 * metadata server-side. Any 400 surfaces as a toast so admins know what to
 * fill before retrying.
 */
import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, AlertTriangle, Clock, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { tasksAPI } from '@/lib/api';
import OneTimeInstanceDialog from './OneTimeInstanceDialog';

const KIND_BY_TITLE = {
  'gst registration': 'registration',
  'gst cancellation': 'cancellation',
  'gst amendment':    'amendment',
  'gst notice':       'notice',
};

const TERMINAL_BY_KIND = {
  registration: new Set(['client_onboarded']),
  cancellation: new Set(['approved', 'cancelled']),
  amendment:    new Set(['approved']),
  notice:       new Set(['resolved']),
};

const daysFromToday = (iso) => {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  return Math.floor((d - new Date(new Date().toDateString())) / 86400000);
};

const getInstanceKindFromSubtaskTitle = (title) =>
  KIND_BY_TITLE[(title || '').trim().toLowerCase()];

export default function OneTimeActivityGrid({
  task, subtask, clients, staffMembers, isAdmin, canManage, onReload,
  STAGE_LABELS, STATUS_SHORT, STATUS_CELL_COLORS,
}) {
  const kind = getInstanceKindFromSubtaskTitle(subtask.title);
  const fy = task.fy_year || '2026-27';
  const periodKey = `OT-${fy}`;
  const activeStages = subtask.custom_stages || [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInstance, setEditingInstance] = useState(null);

  // Only rows with `instance_id` are Phase-2 instances. Older Phase-1 rows
  // (legacy `clients[]` entries without metadata) are still rendered as a
  // simple status row so admins can clean them up via Edit.
  const instances = useMemo(() => {
    const list = (subtask.clients || []).filter(c => !!c.instance_id);
    // Portal-wide rule: alphabetical by client name. Prospects (synthetic
    // client_id "prospect:<uuid>") fall back to prospect_name.
    const nameOf = (row) => {
      if (row.client_id?.startsWith('prospect:')) return (row.prospect_name || 'Prospect').toLowerCase();
      const cl = clients.find(c => c.id === row.client_id);
      return (cl?.name || row.prospect_name || 'unknown').toLowerCase();
    };
    return [...list].sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
  }, [subtask.clients, clients]);

  const legacyRows = useMemo(() => {
    return (subtask.clients || []).filter(c => !c.instance_id);
  }, [subtask.clients]);

  const clientsById = useMemo(() => {
    const m = {};
    clients.forEach(c => { m[c.id] = c; });
    return m;
  }, [clients]);

  const staffById = useMemo(() => {
    const m = {};
    staffMembers.forEach(s => { m[s.id] = s; });
    return m;
  }, [staffMembers]);

  const getRowName = (row) => {
    if (row.client_id?.startsWith('prospect:')) return row.prospect_name || 'Prospect';
    return clientsById[row.client_id]?.name || row.prospect_name || 'Unknown';
  };

  const handleStatusChange = async (instance, newStatus) => {
    try {
      await tasksAPI.updateOneTimeInstance(task.id, subtask.id, instance.instance_id, { status: newStatus });
      toast.success(`Status → ${STAGE_LABELS[newStatus] || newStatus}`);
      onReload?.();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to update status';
      toast.error(msg, { duration: 6000 });
      // If validation failure was due to missing metadata, open the edit dialog
      // so admin can fill in fields and re-try the transition.
      if (err.response?.status === 400) {
        setEditingInstance(instance);
        setDialogOpen(true);
      }
    }
  };

  const handleDelete = async (instance) => {
    if (!window.confirm(`Delete this ${kind} instance? This cannot be undone.`)) return;
    try {
      await tasksAPI.deleteOneTimeInstance(task.id, subtask.id, instance.instance_id);
      toast.success('Instance deleted');
      onReload?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const openAdd = () => { setEditingInstance(null); setDialogOpen(true); };
  const openEdit = (inst) => { setEditingInstance(inst); setDialogOpen(true); };

  // Per-kind extra column header
  const extraColLabel = {
    registration: 'GSTIN / Category',
    cancellation: 'GSTIN / Cancel Date',
    amendment:    'GSTIN / Type',
    notice:       'Notice No / GSTIN',
  }[kind] || 'Details';

  const renderExtra = (row) => {
    if (kind === 'registration') {
      return (
        <div className="flex flex-col">
          <span className="font-mono text-[11px]">{row.gstin || <span className="text-muted-foreground italic">pending</span>}</span>
          <span className="text-[10px] text-muted-foreground">{row.category || 'Regular'}{row.registration_date ? ` · ${row.registration_date}` : ''}</span>
        </div>
      );
    }
    if (kind === 'cancellation') {
      return (
        <div className="flex flex-col">
          <span className="font-mono text-[11px]">{row.gstin || '—'}</span>
          <span className="text-[10px] text-muted-foreground">{row.cancellation_date ? `Effective: ${row.cancellation_date}` : 'Date pending'}</span>
        </div>
      );
    }
    if (kind === 'amendment') {
      return (
        <div className="flex flex-col">
          <span className="font-mono text-[11px]">{row.gstin || '—'}</span>
          <span className="text-[10px] text-muted-foreground truncate max-w-[200px]">{row.amendment_type || '—'}</span>
        </div>
      );
    }
    if (kind === 'notice') {
      return (
        <div className="flex flex-col">
          <span className="font-mono text-[11px]">{row.notice_no || '—'}</span>
          <span className="text-[10px] text-muted-foreground">{row.gstin || '—'}</span>
        </div>
      );
    }
    return '—';
  };

  const renderDueCell = (row) => {
    if (kind !== 'notice') return null;
    if (!row.due_date) return <span className="text-[11px] text-muted-foreground italic">—</span>;
    const left = daysFromToday(row.due_date);
    const status = (row.periods || {})[periodKey];
    const terminal = TERMINAL_BY_KIND[kind].has(status);
    if (terminal) return <span className="text-[11px] text-muted-foreground">{row.due_date}</span>;
    if (left < 0) {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600">
          <AlertTriangle className="h-3 w-3" />{row.due_date} ({Math.abs(left)}d overdue)
        </span>
      );
    }
    if (left <= 3) {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700">
          <Clock className="h-3 w-3" />{row.due_date} ({left}d left)
        </span>
      );
    }
    return <span className="text-[11px] text-muted-foreground">{row.due_date} ({left}d)</span>;
  };

  return (
    <>
      <div className="space-y-3" data-testid="one-time-activity-grid">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">{instances.length} instance{instances.length === 1 ? '' : 's'} · FY {fy}</h3>
            <p className="text-xs text-muted-foreground">Each row is a discrete event. The same client may have multiple instances.</p>
          </div>
          {canManage && (
            <Button onClick={openAdd} className="gap-1.5" data-testid="add-one-time-instance">
              <Plus className="h-4 w-4" /> Add {kind === 'notice' ? 'Notice' : kind === 'registration' ? 'Registration' : kind === 'cancellation' ? 'Cancellation' : 'Amendment'}
            </Button>
          )}
        </div>

        <Card>
          <CardContent className="p-0">
            {instances.length === 0 && legacyRows.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">
                No instances yet. Click <span className="font-semibold">Add</span> to create the first one.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/30 border-b">
                    <tr className="text-left text-xs">
                      <th className="px-4 py-2.5 font-semibold">Client</th>
                      <th className="px-4 py-2.5 font-semibold">{extraColLabel}</th>
                      {kind === 'notice' && <th className="px-4 py-2.5 font-semibold">Reply Due</th>}
                      <th className="px-4 py-2.5 font-semibold">Assigned</th>
                      <th className="px-4 py-2.5 font-semibold">Status</th>
                      <th className="px-4 py-2.5 font-semibold w-24"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {instances.map(inst => {
                      const status = (inst.periods || {})[periodKey] || activeStages[0] || 'not_started';
                      const cellColor = STATUS_CELL_COLORS[status] || 'bg-gray-50 text-gray-500';
                      const assignedName = inst.assigned_to ? (staffById[inst.assigned_to]?.name || staffById[inst.assigned_to]?.email || '—') : '—';
                      return (
                        <tr key={inst.instance_id} className="border-b last:border-b-0 hover:bg-muted/10" data-testid={`one-time-row-${inst.instance_id}`}>
                          <td className="px-4 py-2.5">
                            <div className="flex flex-col">
                              <span className="font-medium text-sm">{getRowName(inst)}</span>
                              {inst.client_id?.startsWith('prospect:') && (
                                <Badge variant="outline" className="self-start text-[9px] px-1 py-0 mt-0.5 bg-amber-50 text-amber-700 border-amber-200">PROSPECT</Badge>
                              )}
                              {inst.remarks && <span className="text-[10px] text-muted-foreground italic mt-0.5 truncate max-w-[260px]" title={inst.remarks}>{inst.remarks}</span>}
                            </div>
                          </td>
                          <td className="px-4 py-2.5">{renderExtra(inst)}</td>
                          {kind === 'notice' && <td className="px-4 py-2.5">{renderDueCell(inst)}</td>}
                          <td className="px-4 py-2.5 text-xs text-muted-foreground">{assignedName}</td>
                          <td className="px-4 py-2.5">
                            {canManage ? (
                              <Select value={status} onValueChange={(v) => handleStatusChange(inst, v)}>
                                <SelectTrigger className={`h-8 w-44 text-[11px] font-bold ${cellColor}`} data-testid={`status-select-${inst.instance_id}`}>
                                  <SelectValue>{STAGE_LABELS[status] || status}</SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  {activeStages.map(key => (
                                    <SelectItem key={key} value={key}><span className="text-xs">{STATUS_SHORT[key]} — {STAGE_LABELS[key] || key}</span></SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Badge className={`text-[10px] ${cellColor}`}>{STAGE_LABELS[status] || status}</Badge>
                            )}
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-1">
                              {canManage && (
                                <button onClick={() => openEdit(inst)} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-primary" title="Edit" data-testid={`edit-${inst.instance_id}`}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                              )}
                              {isAdmin && (
                                <button onClick={() => handleDelete(inst)} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-red-500" title="Delete" data-testid={`delete-${inst.instance_id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {legacyRows.length > 0 && (
                      <tr><td colSpan={kind === 'notice' ? 6 : 5} className="px-4 py-2 text-[11px] text-muted-foreground italic bg-amber-50/30 border-t">
                        {legacyRows.length} legacy row{legacyRows.length === 1 ? '' : 's'} (Phase 1 — no metadata). Re-create as instance{legacyRows.length === 1 ? '' : 's'} to enable due-date tracking.
                      </td></tr>
                    )}
                    {legacyRows.map((row, idx) => {
                      const status = (row.periods || {})[periodKey] || 'not_started';
                      return (
                        <tr key={`legacy-${row.client_id}-${idx}`} className="border-b last:border-b-0 bg-amber-50/10">
                          <td className="px-4 py-2 text-sm">{clientsById[row.client_id]?.name || row.client_id}</td>
                          <td className="px-4 py-2 text-xs text-muted-foreground italic">legacy row</td>
                          {kind === 'notice' && <td className="px-4 py-2"></td>}
                          <td className="px-4 py-2 text-xs">—</td>
                          <td className="px-4 py-2"><Badge className={`text-[10px] ${STATUS_CELL_COLORS[status] || ''}`}>{STAGE_LABELS[status] || status}</Badge></td>
                          <td className="px-4 py-2"></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status legend */}
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          <span className="text-muted-foreground font-medium mr-1"><MessageSquare className="h-3 w-3 inline mr-0.5" />Status legend:</span>
          {activeStages.map(key => (
            <span key={key} className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-medium ${STATUS_CELL_COLORS[key] || 'bg-gray-50 text-gray-500'}`}>
              {STAGE_LABELS[key] || key}
            </span>
          ))}
        </div>
      </div>

      <OneTimeInstanceDialog
        open={dialogOpen}
        onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditingInstance(null); }}
        taskId={task.id}
        subtaskId={subtask.id}
        kind={kind}
        clients={clients}
        instance={editingInstance}
        onSaved={onReload}
      />
    </>
  );
}
